import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeft, FileText, Loader2, Wand2, Download } from "lucide-react";

/**
 * Solicitor-only TA13 (Completion Information & Requisitions on Title).
 * Native form — schema comes from the backend at
 * `/api/cases/:id/ta13`; the same endpoint returns the autofill and
 * any saved draft values. On "Generate PDF" the backend renders a
 * Law-Society-style PDF and files it into the case Documents folder.
 */
export default function CaseTa13Page() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/cases/${caseId}/ta13`);
      setState(r.data);
      setValues(r.data.values || {});
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load TA13");
    }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const scheduleSave = useCallback((next) => {
    setDirty(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/cases/${caseId}/ta13/values`, { values: next });
        setDirty(false);
      } catch { toast.error("Auto-save failed"); }
      finally { setSaving(false); }
    }, 900);
  }, [caseId]);

  const setField = (key, v) => {
    setValues(prev => {
      const next = { ...prev, [key]: v };
      // Auto-recalculate balance_due when the user hasn't manually overridden it.
      if (["purchase_price", "chattels_price", "apportion_rent",
           "apportion_service", "apportion_insurance",
           "less_deposit_paid"].includes(key)) {
        next.balance_due = computeBalance(next);
      }
      scheduleSave(next);
      return next;
    });
  };

  const reAutofill = () => {
    if (!state?.autofill) return;
    if (Object.keys(values).length && !window.confirm(
      "Re-run autofill? This overwrites any fields you've typed.",
    )) return;
    setValues(state.autofill);
    scheduleSave(state.autofill);
    toast.success("Autofill re-applied.");
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/cases/${caseId}/ta13/generate`, { values });
      toast.success("TA13 PDF generated and filed to case Documents.");
      setState(s => ({ ...s, document_id: r.data.document_id }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const autofilledKeys = useMemo(() => new Set(Object.keys(state?.autofill || {})), [state]);

  if (!state) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ta13-page">
      <div className="max-w-4xl mx-auto p-6">
        <button onClick={() => navigate(`/cases/${caseId}`)} data-testid="ta13-back-btn"
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back to case
        </button>

        <div className="mt-4 mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              TA13 — Completion Information & Requisitions on Title
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Autofilled from case data. Edit anything, then generate the PDF — it lands in this case's Documents folder.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-500 w-16 text-right">
              {saving ? "Saving…" : dirty ? "Unsaved" : "Saved"}
            </span>
            <button onClick={reAutofill} data-testid="ta13-reautofill-btn"
              className="px-3 py-2 rounded-lg text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5">
              <Wand2 className="w-3.5 h-3.5" /> Re-run autofill
            </button>
            <button onClick={generate} disabled={generating} data-testid="ta13-generate-btn"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#100f5d] text-white disabled:opacity-50 inline-flex items-center gap-2">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Generate PDF
            </button>
          </div>
        </div>

        {state.document_id && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between"
            data-testid="ta13-latest-pdf">
            <p className="text-sm text-emerald-800">
              Latest TA13 PDF filed to Documents. Regenerate to refresh it with your current values.
            </p>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${state.document_id}/download`}
               target="_blank" rel="noreferrer" data-testid="ta13-download-latest-btn"
               className="text-sm font-semibold text-emerald-800 hover:underline inline-flex items-center gap-1.5">
              <Download className="w-4 h-4" /> Download
            </a>
          </div>
        )}

        <div className="space-y-6">
          {state.sections.map(sec => (
            <section key={sec.title}
              className="rounded-xl border border-slate-200 bg-white overflow-hidden"
              data-testid={`ta13-section-${sec.title.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
              <header className="px-5 py-3 border-b border-slate-100 bg-slate-50/60">
                <h2 className="text-sm font-semibold text-slate-900">{sec.title}</h2>
              </header>
              <div className="p-5 space-y-4">
                {sec.fields.map(f => (
                  <Ta13Field key={f.key}
                    field={f}
                    value={values[f.key]}
                    autofilled={autofilledKeys.has(f.key)}
                    formValues={values}
                    onChange={(v) => setField(f.key, v)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function Ta13Field({ field, value, autofilled, formValues, onChange }) {
  // show_if — hide dependant fields when the gate isn't met.
  if (field.show_if) {
    if (formValues[field.show_if.key] !== field.show_if.equals) return null;
  }
  const testid = `ta13-field-${field.key}`;
  const cls = "w-full text-sm border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#100f5d]/20";

  const labelBlock = (
    <div className="flex items-center gap-2 mb-1.5">
      <label className="text-sm font-medium text-slate-700">{field.label}</label>
      {autofilled && (
        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
          Autofilled
        </span>
      )}
    </div>
  );
  const helpBlock = field.help ? <p className="text-xs text-slate-400 mt-1">{field.help}</p> : null;

  if (field.type === "checkbox") {
    return (
      <div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={value === true || value === "Yes"}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={testid} />
          {field.label}
          {autofilled && <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Autofilled</span>}
        </label>
        {helpBlock}
      </div>
    );
  }
  if (field.type === "radio") {
    return (
      <div>
        {labelBlock}
        <div className="flex gap-4">
          {(field.options || []).map(o => (
            <label key={o} className="inline-flex items-center gap-1.5 text-sm">
              <input type="radio" name={field.key} checked={value === o}
                onChange={() => onChange(o)}
                data-testid={`${testid}-${o.toLowerCase()}`} />
              {o}
            </label>
          ))}
        </div>
        {helpBlock}
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        {labelBlock}
        <textarea className={cls} rows={field.rows || 3} value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid} />
        {helpBlock}
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div>
        {labelBlock}
        <input type="date" className={cls} value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid} />
        {helpBlock}
      </div>
    );
  }
  if (field.type === "currency" || field.type === "number") {
    return (
      <div>
        {labelBlock}
        <input type="number" step="0.01" className={cls} value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          data-testid={testid} />
        {helpBlock}
      </div>
    );
  }
  return (
    <div>
      {labelBlock}
      <input type="text" className={cls} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid} />
      {helpBlock}
    </div>
  );
}

function computeBalance(v) {
  const num = (k) => (v[k] === "" || v[k] === undefined || v[k] === null) ? 0 : Number(v[k]) || 0;
  const total = num("purchase_price") + num("chattels_price")
              + num("apportion_rent") + num("apportion_service") + num("apportion_insurance")
              - num("less_deposit_paid");
  return Math.round(total * 100) / 100;
}
